/**
 * ELEVATION — which neutral a surface wears in dark mode, by its role.
 *
 * Two app layers, a step for what you operate, and a recessed model for
 * modals (design review 2026-10, final pass). Values are the Onyx dark ramp
 * (`brand-colors-dark.json`):
 *
 * | role        | what                                                          | dark          | Onyx    |
 * | ----------- | ------------------------------------------------------------- | ------------- | ------- |
 * | canvas      | the dotted page behind everything                             | `background`  | #070707 |
 * | layer 0     | chrome — sidebar, every top bar + breadcrumb row; the OUTER    | `secondary-50`  | #101012 |
 * |             | shell of widgets, the deck's panes (All Sources, Study tab)    |               |         |
 * | track       | a segmented control's / tab bar's track, a tagged multi-select | `background` at 70% over its host |
 * |             | — always one step BELOW whatever holds it                     |               |         |
 * | layer 1     | content — widget interiors and every clickable item (note,    | `secondary-100` | #16161a |
 * |             | mind map, quiz, flashcard and deck tiles, cards, list rows)   |               |         |
 * | control     | buttons, fields, chips, a highlighted row or active segment — | `secondary-200` at 60% over its host |
 * |             | always one step above whatever holds it                       |               |         |
 * | overlay     | menus, popovers, hover cards                                  | `secondary-100` | #16161a |
 * | modal shell | the dialog's outer frame (DS Dialog glass)                    | `secondary-100`/70 glass |  |
 * | modal body  | a modal's content (DS dialog body, Settings pane…)            | `secondary-50`  | #101012 |
 * | modal well  | the darkest step, only when a modal has THREE layers: the body | `background`  | #070707 |
 * |             | under a grid of tiles (New Study Deck), or a preview / drop    |               |         |
 * |             | zone inside a plain body                                      |               |         |
 *
 * In a modal the order is reversed on purpose: content RECEDES into the
 * shell, and the controls on it are the lightest thing there. Two layers
 * (shell + body) use the lighter body step; with three, the layer UNDER the
 * innermost one goes to the canvas colour, so the innermost (tiles, a
 * preview's frame) keeps the lighter step.
 *
 * These are `dark:` classes on purpose: light mode has its own ladder (grey
 * shell, white body) and keeps it. Append them to a surface's light classes;
 * never use them to restyle light.
 *
 * Trap: a plain `hover:bg-*` LOSES to a `dark:bg-*` fill in the cascade, so an
 * element wearing one of these fills restates its hover as `dark:hover:` (one
 * step up).
 */

/** Layer 0 — chrome and outer shells (widget shells, deck panes, segmented tracks). */
export const DARK_LAYER0 = 'dark:bg-secondary-50';

/**
 * A recessed track — the segmented control's, the deck's source tab bar's, a
 * multi-select holding tags. The canvas at 70%, so it is always a step BELOW
 * whatever holds it: on chrome (layer 0) as on content (layer 1). An opaque
 * layer-0 track vanished on every layer-0 host (review 2026-10).
 */
export const DARK_TRACK = 'dark:bg-background/70';

/** Layer 1 — content: widget interiors and every clickable tile, card and row. */
export const DARK_LAYER1 = 'dark:bg-secondary-100';

/**
 * Layer 1 as glass (the deck tile's frosted footer): the same colour, a little
 * translucent so the cover still glows through the blur.
 */
export const DARK_LAYER1_GLASS = 'dark:bg-secondary-100/85';

/** {@link DARK_LAYER1_GLASS} for an SVG surface, which paints with `fill`. */
export const DARK_LAYER1_GLASS_FILL = 'dark:fill-secondary-100/85';

/**
 * A control (button, field, tag, chat composer) — `secondary-200` at 60%, so it
 * is one step above WHATEVER holds it: chrome, content, an overlay or a modal
 * well.
 */
export const DARK_CONTROL = 'dark:bg-secondary-200/60';

/**
 * {@link DARK_CONTROL} for a control whose classes switch while it is mounted
 * (a selected state, a variant flip, a pressed fill). Web wears the
 * translucent step; native the OPAQUE `secondary-200` — a translucent fill is
 * runtime-processed on native and must never toggle on a live view there,
 * an opaque one may. The app has dark mode too (#781), so native needs it.
 */
export const DARK_CONTROL_TOGGLED =
  'web:dark:bg-secondary-200/60 native:dark:bg-secondary-200';

/** A highlighted row, chip or active cell — the same relative step as a control. */
export const DARK_LIFTED = 'dark:bg-secondary-200/60';

/** A surface that floats over content: menus, popovers, hover cards, the floating chat's chrome. */
export const DARK_OVERLAY = 'dark:bg-secondary-100';

/** A modal's content step — the DS dialog body; darker than the shell, lighter than a well. */
export const DARK_MODAL_PANEL = 'dark:bg-secondary-50';

/** The darkest modal step, for a THREE-layer modal: a body holding tiles, or a preview / drop zone in a body. */
export const DARK_MODAL_WELL = 'dark:bg-background';
